import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { brandVars } from "@/lib/brand";
import { Mic, Copy, Check, Download, AlertTriangle, Calendar, Video, User, Share2, Star, Heart, Linkedin, Facebook, Youtube, Instagram, Globe, Podcast } from "lucide-react";

const resolveUrl = (u) => (u && u.startsWith("/api") ? `${BACKEND_URL}${u}` : u);

const FOLLOW_META = {
  youtube: { label: "YouTube", Icon: Youtube },
  linkedin: { label: "LinkedIn", Icon: Linkedin },
  facebook: { label: "Facebook", Icon: Facebook },
  instagram: { label: "Instagram", Icon: Instagram },
  podcast_page: { label: "Podcast page", Icon: Podcast },
  website: { label: "Website", Icon: Globe },
};

export default function PromoKit() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/promo-kit/${token}`).then(r => setData(r.data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <Shell>
        <div className="pgm-card" style={{ textAlign: "center", padding: 40 }} data-testid="kit-not-found">
          <AlertTriangle size={28} color="var(--pgm-orange)" style={{ margin: "0 auto 12px" }} />
          <h2 className="pgm-display" style={{ fontSize: 26 }}>Promo kit not found</h2>
          <p style={{ opacity: 0.75 }}>This link doesn't match any guest. Please check the link in your email, or contact the show host.</p>
        </div>
      </Shell>
    );
  }
  if (!data) return <Shell><div style={{ textAlign: "center", padding: 40 }}>Loading…</div></Shell>;

  const { guest, promo, kit_images, guest_uploads = [], show, post_show, episode } = data;
  const logoSrc = resolveUrl(show.podcast_logo_url);
  const headshot = resolveUrl(guest.headshot_url);
  const copyBlocks = [
    { key: "episode_title", label: "Episode title", text: promo.episode_title },
    { key: "episode_description", label: "Episode description", text: promo.episode_description },
    { key: "guest_swipe", label: "Social post — LinkedIn / Facebook / Instagram", text: promo.guest_swipe },
    { key: "guest_short_social", label: "Short version — X / Threads / IG Stories / DM", text: promo.guest_short_social },
  ].filter(b => (b.text || "").trim());
  const emailSwipe = (promo.guest_email || "").trim();
  const hasAnyContent = copyBlocks.length > 0 || emailSwipe || kit_images.length > 0 || guest_uploads.length > 0;

  return (
    <Shell brand={show.brand_colors}>
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        {logoSrc ? (
          <img src={logoSrc} alt="show logo" style={{ width: 72, height: 72, objectFit: "cover", border: "2px solid var(--pgm-magenta)", margin: "0 auto 12px", display: "block" }} data-testid="kit-show-logo" />
        ) : (
          <div style={{ width: 72, height: 72, background: "var(--pgm-magenta)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Mic size={30} color="var(--pgm-cream)" />
          </div>
        )}
        <div className="pgm-micro" style={{ marginBottom: 6 }}>◆ {show.show_name || "Guest Promo Kit"}</div>
        <h1 className="pgm-display" style={{ fontSize: 34, lineHeight: 1.15 }} data-testid="kit-heading">
          {post_show ? "Your Episode Is Live!" : "Your Guest Promo Kit"}
        </h1>
        <p style={{ opacity: 0.75, marginTop: 8, fontSize: 14 }}>
          {post_show
            ? "Thanks for an amazing conversation — here's everything to share your episode with the world."
            : "Everything you need to share your upcoming appearance — copy a post, grab the graphics, and you're set."}
        </p>
      </div>

      <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)", marginBottom: 16 }} data-testid="kit-guest-card">
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ width: 88, height: 88, border: "1.5px solid var(--pgm-rule)", background: "var(--pgm-cream)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
            {headshot ? <img src={headshot} alt={guest.full_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={26} style={{ opacity: 0.35 }} />}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="pgm-display" style={{ fontSize: 24 }}>{guest.full_name}</div>
            {guest.title && <div style={{ fontSize: 13, opacity: 0.75, marginTop: 2 }}>{guest.title}</div>}
            <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap", fontSize: 13 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="kit-episode-date">
                <Calendar size={14} color="var(--pgm-magenta)" /> {guest.episode_date || "Date TBD"}{guest.episode_number ? ` · EP ${guest.episode_number}` : ""}
              </span>
              {post_show && episode.episode_link ? (
                <a href={episode.episode_link} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--pgm-magenta)", fontWeight: 700 }} data-testid="kit-episode-link">
                  <Video size={14} /> Watch your episode
                </a>
              ) : guest.broadcast_link ? (
                <a href={guest.broadcast_link} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--pgm-magenta)", fontWeight: 700 }} data-testid="kit-broadcast-link">
                  <Video size={14} /> Your studio link
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {!hasAnyContent ? (
        <div className="pgm-callout" data-testid="kit-empty">
          <strong>Your kit is being prepared.</strong> Check back soon — {show.host_name || "the host"} is putting together your promo copy and graphics.
        </div>
      ) : (
        <>
          {copyBlocks.map(({ key, label, text }) => (
            <CopyCard key={key} id={key} label={label} text={text} />
          ))}
          {emailSwipe && <EmailSwipeCard raw={emailSwipe} />}

          <GuestImagesSection
            token={token}
            hostImages={kit_images}
            guestUploads={guest_uploads}
            onChange={(nextUploads) => setData(prev => ({ ...prev, guest_uploads: nextUploads }))}
          />
        </>
      )}

      {post_show && (episode.episode_link || episode.clips.length > 0 || episode.storage_folder) && (
        <div style={{ marginTop: 26 }} data-testid="kit-episode-section">
          <div className="pgm-divider" style={{ margin: "18px 0 14px" }}><strong>◆ YOUR EPISODE &amp; CLIPS</strong></div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {episode.episode_link && (
              <a href={episode.episode_link} target="_blank" rel="noreferrer" className="pgm-btn" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} data-testid="kit-episode-watch">
                <Video size={14} /> Watch the full episode
              </a>
            )}
            {episode.clips.map((c, i) => (
              <a key={c} href={c} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} data-testid={`kit-clip-${i}`}>
                <Download size={14} /> Clip {i + 1}
              </a>
            ))}
            {episode.storage_folder && (
              <a href={episode.storage_folder} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} data-testid="kit-storage-folder">
                <Download size={14} /> Episode files (video, audio, clips)
              </a>
            )}
          </div>
          <p style={{ fontSize: 12, opacity: 0.65, marginTop: 10 }}>
            Clips are perfect for LinkedIn, Instagram Reels and YouTube Shorts — download and post with your favorite copy above.
          </p>
        </div>
      )}

      <ShareButtons guest={guest} promo={promo} show={show} postShow={post_show} episode={episode} />
      <FollowAndReview show={show} />

      <p style={{ textAlign: "center", fontSize: 12, opacity: 0.55, marginTop: 26 }}>
        Powered by ShowRunner HQ
      </p>
    </Shell>
  );
}

function ShareButtons({ guest, promo, show, postShow, episode }) {
  const socials = show.socials || {};
  const showLink = socials.podcast_page || socials.youtube || socials.website || "";
  let text;
  let shareUrl;
  if (postShow && episode?.episode_link) {
    text = promo.episode_title
      ? `My episode is live! ${promo.episode_title} — on ${show.show_name}. Watch it here:`
      : `My episode on ${show.show_name} is live! Watch it here:`;
    shareUrl = episode.episode_link;
    text += ` ${shareUrl}`;
  } else {
    text = promo.episode_title
      ? `${promo.episode_title} — catch me on ${show.show_name}${guest.episode_date ? ` on ${guest.episode_date}` : ""}!`
      : `Catch me on ${show.show_name}${guest.episode_date ? ` on ${guest.episode_date}` : ""}!`;
    shareUrl = showLink;
    if (showLink) text += ` ${showLink}`;
  }
  const enc = encodeURIComponent(text);
  const links = [
    { key: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/feed/?shareActive=true&text=${enc}` },
    { key: "x", label: "Post on X", Icon: Share2, href: `https://twitter.com/intent/tweet?text=${enc}` },
    ...(shareUrl ? [{ key: "facebook", label: "Share on Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${enc}` }] : []),
  ];
  return (
    <div style={{ marginTop: 26 }} data-testid="kit-share-section">
      <div className="pgm-divider" style={{ margin: "18px 0 14px" }}><strong>◆ {postShow ? "SHARE YOUR EPISODE" : "SHARE YOUR APPEARANCE"}</strong></div>
      <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 12 }}>
        One click opens a pre-filled post — or paste in your favorite copy from above.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {links.map(({ key, label, Icon, href }) => (
          <a key={key} href={href} target="_blank" rel="noreferrer" className="pgm-btn" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} data-testid={`kit-share-${key}`}>
            <Icon size={14} /> {label}
          </a>
        ))}
      </div>
    </div>
  );
}

function FollowAndReview({ show }) {
  const socials = show.socials || {};
  const follows = Object.entries(FOLLOW_META)
    .filter(([k]) => (socials[k] || "").trim())
    .map(([k, meta]) => ({ key: k, url: socials[k], ...meta }));
  const reviews = [
    socials.apple_podcasts && { key: "apple", label: "Review on Apple Podcasts", url: socials.apple_podcasts },
    socials.spotify && { key: "spotify", label: "Rate on Spotify", url: socials.spotify },
  ].filter(Boolean);
  if (follows.length === 0 && reviews.length === 0) return null;
  return (
    <div style={{ marginTop: 26 }} data-testid="kit-follow-section">
      <div className="pgm-divider" style={{ margin: "18px 0 14px" }}><strong>◆ SUPPORT {(show.show_name || "THE SHOW").toUpperCase()}</strong></div>
      {follows.length > 0 && (
        <>
          <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Heart size={13} color="var(--pgm-magenta)" /> Subscribe &amp; follow so you never miss your episode:
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: reviews.length ? 18 : 0 }}>
            {follows.map(({ key, label, Icon, url }) => (
              <a key={key} href={url} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }} data-testid={`kit-follow-${key}`}>
                <Icon size={14} /> {label}
              </a>
            ))}
          </div>
        </>
      )}
      {reviews.length > 0 && (
        <>
          <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <Star size={13} color="var(--pgm-magenta)" /> Enjoyed being on the show? A quick rating means the world:
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {reviews.map(({ key, label, url }) => (
              <a key={key} href={url} target="_blank" rel="noreferrer" className="pgm-btn pgm-btn-ghost" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8, borderColor: "var(--pgm-olive)", color: "var(--pgm-olive)" }} data-testid={`kit-review-${key}`}>
                <Star size={14} /> {label}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return Promise.resolve(fallbackCopy(text));
}

function fallbackCopy(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } catch (e) { console.warn("Clipboard fallback failed:", e); }
  document.body.removeChild(ta);
}

function CopyCard({ id, label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    copyText(text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)", marginBottom: 14 }} data-testid={`kit-copy-${id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div className="pgm-micro">◆ {label}</div>
        <button className="pgm-btn pgm-btn-ghost" style={{ padding: "6px 12px", fontSize: 11 }} onClick={copy} data-testid={`kit-copy-btn-${id}`}>
          {copied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
      <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, color: "var(--pgm-ink)", lineHeight: 1.55, margin: 0 }}>{text}</pre>
    </div>
  );
}

// Parse SUBJECT 1/2/3 + BODY from the AI-generated guest_email string.
function parseGuestEmailKit(raw) {
  if (!raw) return { subjects: [], body: "" };
  const text = raw.replace(/\r\n/g, "\n").trim();
  const subjectRe = /^SUBJECT\s*(\d+)\s*[—\-–:]\s*(.+)$/gim;
  const subjects = [];
  let match;
  while ((match = subjectRe.exec(text)) !== null) {
    subjects.push(match[2].trim());
  }
  const bodyMarkerIdx = text.search(/^\s*BODY\s*$/im);
  let body = "";
  if (bodyMarkerIdx >= 0) {
    body = text.slice(bodyMarkerIdx).replace(/^\s*BODY\s*\n?/i, "").trim();
  } else if (subjects.length > 0) {
    const all = [...text.matchAll(subjectRe)];
    const last = all[all.length - 1];
    body = text.slice(last.index + last[0].length).trim();
  } else {
    body = text;
  }
  return { subjects: subjects.slice(0, 3), body };
}

function EmailSwipeCard({ raw }) {
  const { subjects, body } = parseGuestEmailKit(raw);
  const [copied, setCopied] = useState("");
  const doCopy = (text, tag) => {
    copyText(text || "");
    setCopied(tag);
    setTimeout(() => setCopied(""), 1600);
  };
  if (subjects.length === 0 && !body) {
    return <CopyCard id="guest_email" label="Email swipe for your newsletter" text={raw} />;
  }
  return (
    <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)", marginBottom: 14 }} data-testid="kit-copy-guest_email">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div className="pgm-micro">◆ Email swipe for your newsletter</div>
        <button
          className="pgm-btn pgm-btn-ghost"
          style={{ padding: "6px 12px", fontSize: 11 }}
          onClick={() => doCopy(raw, "all")}
          data-testid="kit-copy-btn-guest_email"
        >
          {copied === "all" ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy all</>}
        </button>
      </div>
      <p style={{ fontSize: 12, opacity: 0.7, margin: "0 0 12px 0" }}>Pick a subject line, then paste the body into your newsletter. All three subject lines lead to the same email — the tone is just different.</p>
      {subjects.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }} data-testid="kit-email-subjects">
          {subjects.map((s, i) => (
            <div key={`subj-${i}-${(s || "").slice(0, 24)}`} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "var(--pgm-cream)", padding: 10, borderLeft: "2px solid var(--pgm-magenta)" }}>
              <div style={{ minWidth: 68, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)", paddingTop: 3 }}>
                Subject {i + 1}
              </div>
              <div style={{ flex: 1, fontSize: 13.5, color: "var(--pgm-ink)" }} data-testid={`kit-email-subject-${i}`}>{s}</div>
              <button
                className="pgm-btn pgm-btn-ghost"
                style={{ padding: "3px 10px", fontSize: 10 }}
                onClick={() => doCopy(s, `s${i}`)}
                data-testid={`kit-email-subject-copy-${i}`}
              >
                {copied === `s${i}` ? <><Check size={10} /> Copied!</> : <><Copy size={10} /> Copy</>}
              </button>
            </div>
          ))}
        </div>
      )}
      {body && (
        <div style={{ background: "#fff", border: "1px solid var(--pgm-rule)", padding: 12 }} data-testid="kit-email-body">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--pgm-magenta)" }}>Body</div>
            <button
              className="pgm-btn pgm-btn-ghost"
              style={{ padding: "3px 10px", fontSize: 10 }}
              onClick={() => doCopy(body, "body")}
              data-testid="kit-email-body-copy"
            >
              {copied === "body" ? <><Check size={10} /> Copied!</> : <><Copy size={10} /> Copy body</>}
            </button>
          </div>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Arial", fontSize: 13.5, color: "var(--pgm-ink)", lineHeight: 1.6, margin: 0 }}>{body}</pre>
        </div>
      )}
    </div>
  );
}

function GuestImagesSection({ token, hostImages, guestUploads, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const hasHost = (hostImages || []).length > 0;
  const hasGuest = (guestUploads || []).length > 0;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setError(""); setUploading(true);
    try {
      let latest = guestUploads || [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post(`/promo-kit/${token}/uploads`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latest = data.guest_uploads || latest;
      }
      onChange(latest);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Try a smaller file (10MB max).");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeUpload = async (url) => {
    setError("");
    try {
      const { data } = await api.delete(`/promo-kit/${token}/uploads`, { params: { url } });
      onChange(data.guest_uploads || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to remove image.");
    }
  };

  return (
    <div style={{ marginTop: 8 }} data-testid="kit-images-section">
      {hasHost && (
        <div style={{ marginBottom: 20 }}>
          <div className="pgm-divider" style={{ margin: "18px 0 14px" }}><strong>◆ GRAPHICS FROM YOUR HOST</strong></div>
          <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0, marginBottom: 14 }}>
            {hostImages.length === 1 ? "One graphic your host" : `${hostImages.length} graphics your host`} made for you to share. Right-click to save or hit download.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }} data-testid="kit-images">
            {hostImages.map((img, i) => (
              <div key={img.url} className="pgm-card" style={{ padding: 10 }} data-testid={`kit-image-${i}`}>
                <img src={resolveUrl(img.url)} alt={img.name} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
                <a href={resolveUrl(img.url)} download={img.name} className="pgm-btn pgm-btn-ghost" style={{ width: "100%", justifyContent: "center", marginTop: 10, padding: "8px 12px", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }} data-testid={`kit-image-download-${i}`}>
                  <Download size={12} /> Download
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <div className="pgm-divider" style={{ margin: "18px 0 14px" }}><strong>◆ YOUR OWN IMAGES</strong></div>
        <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0, marginBottom: 12 }}>
          Have a headshot, quote card or promo tile you want to use with the copy above? Upload it here so you always know where it is. Your host can also see what you uploaded.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            data-testid="kit-guest-upload-input"
          />
          <button
            type="button"
            className="pgm-btn"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="kit-guest-upload-btn"
          >
            <Download size={12} style={{ transform: "rotate(180deg)" }} /> {uploading ? "Uploading…" : "Upload an image"}
          </button>
          <span style={{ fontSize: 11, opacity: 0.6, alignSelf: "center" }}>JPG or PNG · 10MB max</span>
        </div>
        {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 12, fontSize: 12 }} data-testid="kit-guest-upload-error">{error}</div>}
        {hasGuest ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }} data-testid="kit-guest-uploads">
            {guestUploads.map((img, i) => (
              <div key={img.url} className="pgm-card" style={{ padding: 10, position: "relative" }} data-testid={`kit-guest-upload-${i}`}>
                <img src={resolveUrl(img.url)} alt={img.name || "upload"} style={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  <a href={resolveUrl(img.url)} download={img.name} className="pgm-btn pgm-btn-ghost" style={{ flex: 1, justifyContent: "center", padding: "8px 10px", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }} data-testid={`kit-guest-upload-download-${i}`}>
                    <Download size={11} /> Download
                  </a>
                  <button
                    type="button"
                    className="pgm-btn pgm-btn-ghost"
                    style={{ padding: "8px 10px", fontSize: 11, borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }}
                    onClick={() => removeUpload(img.url)}
                    data-testid={`kit-guest-upload-remove-${i}`}
                    aria-label="Remove image"
                  >
                    <AlertTriangle size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: 20, border: "1px dashed var(--pgm-rule)", textAlign: "center", fontSize: 12.5, color: "var(--pgm-ink)", opacity: 0.7 }}>
            No uploads yet — click the button above to add your own promo images.
          </div>
        )}
      </div>
    </div>
  );
}



function Shell({ children, brand }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--pgm-cream)", padding: "48px 20px", ...brandVars(brand) }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>{children}</div>
    </div>
  );
}
